import { config } from './config'

const API_BASE = config.apiUrl

export function apiUrl(path) {
  // When API_BASE includes a subpath (e.g. /api/kagami), strip the /api prefix
  // from the path to avoid double /api (e.g. /api/kagami/api/auth/me)
  if (API_BASE && API_BASE.includes('/api/') && path.startsWith('/api')) {
    return `${API_BASE}${path.slice(4)}`
  }
  return `${API_BASE}${path}`
}

const RETURN_URL_KEY = 'mcp_return_url'

// Trusted origins for post-login redirects / session bridging: the app's own
// origin, the API origin, and the MCP authorization-server origin (where the
// OAuth /authorize + /mcp-oauth/session endpoints live). Anything else is
// rejected so a crafted `?returnUrl=https://evil.example` can't open-redirect
// (or, worse, receive the bridged session token).
function trustedOrigins() {
  const origins = new Set([window.location.origin])
  for (const candidate of [config.apiUrl, config.mcpUrl]) {
    if (!candidate) continue
    try {
      origins.add(new URL(candidate, window.location.origin).origin)
    } catch (_) {
      // ignore malformed config value
    }
  }
  return origins
}

export function isAllowedReturnUrl(url) {
  if (!url) return false
  try {
    const target = new URL(url, window.location.origin)
    if (target.protocol !== 'https:' && target.protocol !== 'http:') return false
    return trustedOrigins().has(target.origin)
  } catch (_) {
    return false
  }
}

// Continue an in-progress MCP OAuth authorization after web login. `returnUrl`
// is the AS `/authorize` URL. If it's on this same origin the auth cookie is
// already present, so we redirect straight there. If it's on a different
// (trusted) origin — e.g. a tunnel or dedicated MCP host — we bridge the
// session: a top-level POST to `${origin}/mcp-oauth/session` sets the cookie
// first-party on the AS origin, which then redirects back to `returnUrl`.
export function completeOAuthReturn(returnUrl) {
  const target = new URL(returnUrl, window.location.origin)
  if (target.origin === window.location.origin) {
    window.location.href = returnUrl
    return
  }

  const form = document.createElement('form')
  form.method = 'POST'
  form.action = `${target.origin}/mcp-oauth/session`
  const addField = (name, value) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value ?? ''
    form.appendChild(input)
  }
  addField('token', getAuthToken())
  addField('redirect', returnUrl)
  document.body.appendChild(form)
  form.submit()
}

// Persist a validated returnUrl across the (possibly multi-page) login flow.
export function setReturnUrl(url) {
  if (isAllowedReturnUrl(url)) {
    sessionStorage.setItem(RETURN_URL_KEY, url)
  }
}

// Retrieve and clear a previously stored returnUrl, re-validating it.
export function popReturnUrl() {
  const url = sessionStorage.getItem(RETURN_URL_KEY)
  sessionStorage.removeItem(RETURN_URL_KEY)
  return isAllowedReturnUrl(url) ? url : null
}

export function setAuthToken(token) {
  if (token) {
    localStorage.setItem('auth_token', token)
  } else {
    localStorage.removeItem('auth_token')
  }
}

export function getAuthToken() {
  const fromStorage = localStorage.getItem('auth_token')
  if (fromStorage) return fromStorage

  // localStorage can be evicted while the session is still valid (Safari ITP,
  // privacy modes, a different frontend origin). The backend issues the same
  // JWT as the readable `auth` cookie, so fall back to it to stay authenticated.
  const match = document.cookie.match(/(?:^|;\s*)auth=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

// Event dispatched when the API reports an expired session. UserProvider listens
// for it and routes the user back to /login with a "session expired" message.
// Using an event (rather than a redirect here) keeps navigation in the React
// layer so it respects the router basename and doesn't force a full reload.
export const SESSION_EXPIRED_EVENT = 'kagami:session-expired'

export async function apiFetch(path, options = {}) {
  const token = getAuthToken()
  const headers = {
    ...(options.headers || {}),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  }

  // Auto-set Content-Type for JSON string bodies if not already set
  if (options.body && typeof options.body === 'string' && !headers['Content-Type'] && !headers['content-type']) {
    try {
      JSON.parse(options.body)
      headers['Content-Type'] = 'application/json'
    } catch (_) {
      // Not JSON, leave as-is
    }
  }

  const response = await fetch(apiUrl(path), {
    credentials: 'include',
    ...options,
    headers,
  })

  // A 401 tagged SESSION_EXPIRED means a previously-valid session timed out.
  // Drop the stale token and signal the app to redirect to login. Clone so the
  // caller can still read the body. A plain 401 (never logged in / bad creds)
  // is left for the caller to handle.
  if (response.status === 401) {
    try {
      const data = await response.clone().json()
      if (data?.code === 'SESSION_EXPIRED') {
        setAuthToken(null)
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
      }
    } catch (_) {
      // Non-JSON 401 body — not a session-expiry signal, ignore.
    }
  }

  return response
}
